
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import EditMenusSubheader from './EditMenusSubheader.vue';
import ItemList from '@/components/ItemList.vue';
import MenuList from '@/components/MenuList.vue';
import SectionList from '@/components/SectionList.vue';

@Component<EditMenus>({
	components: {
		EditMenusSubheader,
		ItemList,
		MenuList,
		SectionList
	}
})
export default class EditMenus extends Vue {
	@Getter('getMenus', { namespace: 'menus' }) private menus!: Menu[];
	@Prop({ type: Object, required: true }) private restaurant!: Tenant;
	@Prop({ type: Boolean, default: false }) private isActiveTab!: boolean;

	private listComponents = {
		menu: MenuList,
		section: SectionList,
		item: ItemList
	};

	$refs!: {
		menuList: MenuList;
		sectionList: SectionList;
		itemList: ItemList;
	};

	/**
	 * Get the selected menu from the route parameters
	 *
	 * @return {Menu | null}
	 */
	private get selectedMenu(): Menu | null {
		if (this.$route.params.menu_id) {
			return this.menus.find((menu: Menu) => menu.id === Number(this.$route.params.menu_id)) || null;
		}
		return null;
	}


	/**
	 * Get the selected section from the selected menu and route parameters
	 *
	 * @return {MenuSection | null}
	 */
	private get selectedSection(): MenuSection | null {
		if (this.selectedMenu && this.$route.params.section_id) {
			return this.selectedMenu.sections?.find((section) => section.id === Number(this.$route.params.section_id)) || null;
		}
		return null;
	}

	/**
	 * Get the current component to render based on the selected menu/section.
	 *
	 * @return {string}
	 */
	private get currentComponent(): string {
		if (this.selectedSection) {
			return 'item';
		}
		else if (this.selectedMenu) {
			return this.selectedMenu.allow_sections ? 'section' : 'item';
		}
		return 'menu';
	}

	/**
	 * Update route based on the selected menu
	 * The selectedMenu computed property is used to set the selected menu
	 *
	 * @param {Menu} menu - The selected menu.
	 * @return {void}
	 */
	private handleMenuSelected(menu: Menu): void {
		if (menu.sections?.length) {
			this.$router.push(`/menus/${menu.id}/sections`).catch(() => {});
		}
		else {
			this.$router.push(`/menus/${menu.id}/items`).catch(() => {});
		}
	}

	/**
	 * Update route based on the selected section
	 * The selectedSection computed property is used to set the selected section
	 *
	 * @param {MenuSection} section - The selected section.
	 * @return {void}
	 */
	private handleSectionSelected(section: MenuSection): void {
		if (this.selectedMenu) {
			this.$router.push(`/menus/${section.menu_id}/sections/${section.id}/items`).catch(() => {});
		}
	}

	/**
	 * Update the route based on what component is currently active and the selected menu/section
	 * Section items will return to their respective section page
	 * Menus items and sections will return to their respective menu page
	 * Back button is not displayed on menu pages
	 *
	 * @return {void}
	 */
	private handleBack(): void {
		if (this.currentComponent === 'item' && this.selectedSection && this.selectedMenu) {
			this.$router.push(`/menus/${this.selectedMenu.id}/sections`).catch(() => {});
		}
		else {
			this.$router.push('/menus').catch(() => {});
		}
	}

	/**
	 * Opens the editor for the currently selected list component
	 *
	 * @return {void}
	 */
	private handleCreate(): void {
		switch (this.currentComponent) {
			case 'menu':
				this.$refs.menuList.openEditor(true);
				break;
			case 'section':
				this.$refs.sectionList.openEditor(true);
				break;
			case 'item':
				this.$refs.itemList.openEditor(true);
				break;
		}
	}
}
